/**
 * TreeMapAIAssistant Component
 *
 * AI-powered suggestions for creating spheres
 */

import React, { useState } from 'react';
import { useTranslation } from 'localization';
import type { TreeMapAIAssistantProps } from '../../organisms/TreeMap/types';
import { resolveLabel } from '../../../utils/resolveLabel';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './TreeMapAIAssistant.scss';

const TreeMapAIAssistant: React.FC<TreeMapAIAssistantProps> = ({
  context,
  onSuggestionAccept,
  onSuggestionCustomize,
  onClose,
}) => {
  const { i18n } = useTranslation();
  const [userMessage, setUserMessage] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSendMessage = async () => {
    setIsLoading(true);
    // AI API integration pending - using mock data for now
    setTimeout(() => {
      setSuggestions([
        {
          id: '1',
          name: 'Example Suggestion 1',
          description: 'This is a sample AI-generated suggestion',
          properties: {},
          confidence: 0.85,
          rationale: 'Based on your context...',
        },
      ]);
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className="tree-map-ai-assistant">
      <div className="tree-map-ai-assistant__context">
        <h4>Context</h4>
        {context.parent_sphere && (
          <ul>
            <li>Parent: {context.parent_sphere.properties.name}</li>
            <li>Level: {resolveLabel(context.level?.label, i18n.language)}</li>
          </ul>
        )}
      </div>

      <div className="tree-map-ai-assistant__chat">
        <div className="tree-map-ai-assistant__messages">
          {suggestions.length === 0 && !isLoading && (
            <p className="text-muted">
              Ask me to suggest items for this level. For example: "Help me define priorities to
              grow revenue"
            </p>
          )}

          {suggestions.map((suggestion) => (
            <div key={suggestion.id} className="tree-map-ai-assistant__suggestion">
              <h5>{suggestion.name}</h5>
              <p>{suggestion.description}</p>
              <p className="text-muted text-sm">Confidence: {(suggestion.confidence * 100).toFixed(0)}%</p>
              <div className="tree-map-ai-assistant__suggestion-actions">
                <ButtonAtom
                  label="Use This"
                  variant="primary"
                  onClick={() => onSuggestionAccept(suggestion)}
                />
                <ButtonAtom
                  label="Customize"
                  variant="secondary"
                  onClick={() => onSuggestionCustomize(suggestion)}
                />
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="tree-map-ai-assistant__loading">
              <i className="pi pi-spin pi-spinner" />
              <p>Generating suggestions...</p>
            </div>
          )}
        </div>

        <div className="tree-map-ai-assistant__input">
          <input
            type="text"
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            placeholder="Type your message..."
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSendMessage();
              }
            }}
          />
          <ButtonAtom label="Send" icon="pi-send" onClick={handleSendMessage} />
        </div>
      </div>
    </div>
  );
};

export default TreeMapAIAssistant;
